/* =========================================================
   Zamat — Animations
   Vanilla JS, no dependencies. Include once, after the DOM.
   Pairs with the CSS transitions/keyframes already present
   in each section's .scss file.
   ========================================================= */
(function () {
  'use strict';

  var EASE_OUT = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------
     1. SCROLL REVEAL
     Most sections fade up as they enter the viewport.
     Markup: add data-reveal to any element.
     Add data-reveal-stagger="80" on a PARENT to reveal its
     direct children one-by-one, 80ms apart (restaurant cards,
     sponsor logo cards, program list rows, artist cards).
     --------------------------------------------------------- */
  function initReveal() {
    var items = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (!items.length) return;

    items.forEach(function (el) {
      var stagger = el.hasAttribute('data-reveal-stagger');
      var targets = stagger ? Array.prototype.slice.call(el.children) : [el];
      targets.forEach(function (t) {
        t.style.opacity = '0';
        t.style.transform = 'translateY(28px) rotate(-0.4deg)';
        t.style.willChange = 'opacity, transform';
      });
    });

    if (reduce) { // honour reduced-motion: show everything instantly
      document.querySelectorAll('[data-reveal], [data-reveal-stagger] > *')
        .forEach(function (t) { t.style.opacity = ''; t.style.transform = ''; });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var step = parseInt(el.getAttribute('data-reveal-stagger') || '0', 10);
        var targets = step ? Array.prototype.slice.call(el.children) : [el];

        targets.forEach(function (t, i) {
          t.style.transition = 'opacity 620ms ' + EASE_OUT + ' ' + (i * step) + 'ms, ' +
                               'transform 620ms ' + EASE_OUT + ' ' + (i * step) + 'ms';
          t.style.opacity = '1';
          t.style.transform = 'translateY(0) rotate(0deg)';
        });
        io.unobserve(el); // reveal once only
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });

    items.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------
     2. STAT COUNTERS  (Fesztiválról section)
     Numbers roll 0 → target when the section scrolls in.
     Markup: <span data-count-to="40" data-count-suffix="+">0</span>
     --------------------------------------------------------- */
  function initCounters() {
    var nums = document.querySelectorAll('[data-count-to]');
    if (!nums.length) {
      // Fallback for existing markup where counter targets are plain text.
      nums = document.querySelectorAll('.about__stat-num');
      nums.forEach(function (el) {
        var raw = (el.textContent || '').trim();
        var match = raw.match(/-?\d+(?:[\.,]\d+)?/);
        if (!match) return;
        var value = match[0].replace(',', '.');
        var suffix = raw.replace(match[0], '').trim();
        el.setAttribute('data-count-to', value);
        if (suffix) {
          el.setAttribute('data-count-suffix', suffix);
        }
      });
      nums = document.querySelectorAll('[data-count-to]');
    }

    if (!nums.length) return;

    var run = function (el) {
      var target = parseFloat(el.getAttribute('data-count-to'));
      var suffix = el.getAttribute('data-count-suffix') || '';
      var dur = 1100;
      var t0 = null;

      function frame(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min((ts - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        el.textContent = Math.round(target * eased) + suffix;
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    };

    if (reduce) {
      nums.forEach(function (el) {
        el.textContent = el.getAttribute('data-count-to') + (el.getAttribute('data-count-suffix') || '');
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        run(e.target);
        io.unobserve(e.target); // trigger once
      });
    }, { threshold: 0.5 });

    nums.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------
     3. FESTIVAL IMAGE PARALLAX  (Fesztiválról section)
     The blob-masked photo drifts vertically against the
     terracotta blob behind it as the section passes through
     the viewport: above centre → photo sits higher,
     section centred → photo perfectly centred,
     below centre → photo sits lower.
     Markup: data-parallax on the SECTION,
             data-parallax-target on the masked <img>.
     --------------------------------------------------------- */
  function initParallax() {
    var section = document.querySelector('[data-parallax]') || document.querySelector('.about');
    if (!section || reduce) return;
    var img = section.querySelector('[data-parallax-target]') || section.querySelector('.about__shape--img');
    if (!img) return;

    var MAX = 26; // px of drift each way
    var ticking = false;

    function update() {
      var r = section.getBoundingClientRect();
      var vh = window.innerHeight;
      var sectionCentre = r.top + r.height / 2;
      // -1 when section centre is below viewport, +1 when above
      var progress = (vh / 2 - sectionCentre) / (vh / 2 + r.height / 2);
      progress = Math.max(-1, Math.min(1, progress));
      img.style.transform = 'translateY(' + (progress * MAX) + 'px)';
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------------------------------------------------------
     4. HAMBURGER COLOUR SWAP
     The fixed hamburger button inverts (white pill + terracotta
     bars) while it overlaps a terracotta section, and swaps back
     over light backgrounds. 420ms colour transition lives in
     nav-menu.scss.
     Markup: data-dark-section on every terracotta section
             (Fellépők, Hírlevél, Footer, hero slider).
     --------------------------------------------------------- */
  function initHamburgerSwap() {
    var btn = document.querySelector('[data-hamburger]') || document.querySelector('.mobile-menu-btn');
    if (!btn) return;
    var darks = document.querySelectorAll('[data-dark-section]');
    if (!darks.length) {
      darks = document.querySelectorAll('.artists, .newsletter, .site-footer');
    }
    if (!darks.length) return;

    var TERRACOTTA = '#CF5331', WHITE = '#FFFFFF';
    var ticking = false;

    function update() {
      var b = btn.getBoundingClientRect();
      var mid = b.top + b.height / 2;
      var onDark = false;

      darks.forEach(function (s) {
        var r = s.getBoundingClientRect();
        if (mid >= r.top && mid <= r.bottom) onDark = true;
      });

      btn.style.backgroundColor = onDark ? WHITE : TERRACOTTA;
      btn.querySelectorAll('span').forEach(function (bar) {
        bar.style.backgroundColor = onDark ? TERRACOTTA : WHITE;
      });
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------------------------------------------------------
     5. HERO SLIDER CROSSFADE
     Two background images alternate every 6s with a 1.4s
     opacity crossfade (transition lives in slider.scss).
     Markup: .hero-slider__slide elements; active one gets
             .hero-slider__slide--active.
     --------------------------------------------------------- */
  function initHeroSlider() {
    var slides = document.querySelectorAll('.hero-slider__slide');
    if (slides.length < 2 || reduce) return;
    var i = 0;
    setInterval(function () {
      slides[i].classList.remove('hero-slider__slide--active');
      i = (i + 1) % slides.length;
      slides[i].classList.add('hero-slider__slide--active');
    }, 6000);
  }

  /* ---------------------------------------------------------
     6. RESTAURANT MODAL  (bottom sheet)
     Opens on card click, slides up 400ms, LOCKS page scroll
     (position:fixed body technique — prevents the background
     scrolling while you drag the sheet). Closes on backdrop
     click, ✕, Escape, or a downward drag of >90px.
     --------------------------------------------------------- */
  function initRestaurantModal() {
    var modal = document.querySelector('.restaurant-modal');
    var backdrop = document.querySelector('.restaurant-modal-backdrop');
    if (!modal || !backdrop) return;

    var scrollY = 0;

    function lock() {
      scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = -scrollY + 'px';
      document.body.style.width = '100%';
    }
    function unlock() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    }

    function open(card) {
      if (card) {
        modal.querySelector('.restaurant-modal__name').textContent = card.dataset.name || '';
        modal.querySelector('.restaurant-modal__desc').textContent = card.dataset.desc || '';
        var sourceImg = card.querySelector('img');
        var modalImage = modal.querySelector('.restaurant-modal__photo img');
        if (modalImage) {
          modalImage.src = sourceImg ? sourceImg.src : '';
          modalImage.alt = card.dataset.name || '';
        }
      }

      lock();
      modal.classList.add('restaurant-modal--open');
      backdrop.classList.add('restaurant-modal-backdrop--open');
    }
    function close() {
      modal.classList.remove('restaurant-modal--open');
      backdrop.classList.remove('restaurant-modal-backdrop--open');
      modal.style.transform = '';
      unlock();
    }

    document.querySelectorAll('.restaurant-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault();
        open(card);
      });
    });
    backdrop.addEventListener('click', close);
    document.querySelectorAll('[data-modal-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    if (!document.querySelectorAll('[data-modal-close]').length) {
      var fallbackClose = modal.querySelector('.restaurant-modal__close');
      if (fallbackClose) {
        fallbackClose.addEventListener('click', close);
      }
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    // drag-down-to-dismiss — only the sheet moves, background stays put
    var startY = null;
    modal.addEventListener('touchstart', function (e) {
      startY = e.touches[0].clientY;
      modal.style.transition = 'none';
    }, { passive: true });

    modal.addEventListener('touchmove', function (e) {
      if (startY === null) return;
      var dy = e.touches[0].clientY - startY;
      if (dy > 0) {
        e.preventDefault(); // stops the page behind from scrolling
        modal.style.transform = 'translate(-50%, ' + dy + 'px)';
      }
    }, { passive: false });

    modal.addEventListener('touchend', function (e) {
      if (startY === null) return;
      var dy = e.changedTouches[0].clientY - startY;
      modal.style.transition = 'transform 400ms ' + EASE_OUT;
      if (dy > 90) close();
      else modal.style.transform = 'translate(-50%, 0)';
      startY = null;
    });
  }

  /* ---------------------------------------------------------
     7. FAQ ACCORDION
     max-height 0 ↔ scrollHeight, 480ms ease-out; chevron
     rotates 180°. Only one panel open at a time.
     --------------------------------------------------------- */
  function initFaq() {
    document.querySelectorAll('.faq-item__question').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.faq-item');
        var panel = item.querySelector('.faq-item__answer');
        var isOpen = item.classList.contains('faq-item--open');

        document.querySelectorAll('.faq-item--open').forEach(function (o) {
          o.classList.remove('faq-item--open');
          o.querySelector('.faq-item__answer').style.maxHeight = '0px';
          o.querySelector('svg').style.transform = 'rotate(0deg)';
        });

        if (!isOpen) {
          item.classList.add('faq-item--open');
          panel.style.maxHeight = panel.scrollHeight + 'px';
          btn.querySelector('svg').style.transform = 'rotate(180deg)';
        }
      });
    });
  }

  /* --------------------------------------------------------- */
  function init() {
    initReveal();
    initCounters();
    initParallax();
    initHamburgerSwap();
    initHeroSlider();
    initRestaurantModal();
    initFaq();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
